import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Pagination,
    Card,
    CardContent,
} from "@mui/material";
import { EditOutlined, DeleteOutline } from "@mui/icons-material";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../../firebase";
import Swal from "sweetalert2";

const PAGE_SIZE = 10;

function Otherincome() {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    const [income, setIncome] = useState([]);
    const [incomeHeads, setIncomeHeads] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const [form, setForm] = useState({
        incomeHeadId: "",
        date: new Date().toISOString().split("T")[0],
        amount: "",
        description: "",
        userId: currentUser?.uid || "",
    });

    // Fetch income heads
    useEffect(() => {
        const fetchIncomeHeads = async () => {
            const snap = await getDocs(collection(db, "incomeHeads"));
            setIncomeHeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        };
        fetchIncomeHeads();
    }, []);

    // Fetch other income
    useEffect(() => {
        const fetchIncome = async () => {
            const snap = await getDocs(collection(db, "otherIncome"));
            setIncome(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        };
        fetchIncome();
    }, []);

    const handleChange = e => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSave = async () => {
        if (!form.incomeHeadId || !form.date || !form.amount) {
            Swal.fire("Error", "Please fill all required fields", "error");
            return;
        }

        try {
            const saveData = {
                ...form,
                userId: currentUser?.uid || "", // auto-fill logged-in user
                date: Timestamp.fromDate(new Date(form.date)),
            };

            if (editingId) {
                await updateDoc(doc(db, "otherIncome", editingId), saveData);
                Swal.fire("Success", "Income updated", "success");
            } else {
                await addDoc(collection(db, "otherIncome"), saveData);
                Swal.fire("Success", "Income added", "success");
            }

            setOpenDialog(false);
            setEditingId(null);
            setForm({
                incomeHeadId: "",
                date: new Date().toISOString().split("T")[0],
                amount: "",
                description: "",
                userId: currentUser?.uid || "",
            });

            // Reload data
            const snap = await getDocs(collection(db, "otherIncome"));
            setIncome(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            Swal.fire("Error", err.message, "error");
        }
    };

    const handleEdit = inc => {
        setEditingId(inc.id);
        setForm({
            incomeHeadId: inc.incomeHeadId,
            date: inc.date?.toDate().toISOString().split("T")[0],
            amount: inc.amount,
            description: inc.description || "",
            userId: inc.userId || currentUser?.uid || "",
        });
        setOpenDialog(true);
    };

    const handleDelete = async id => {
        const res = await Swal.fire({
            title: "Delete income?",
            icon: "warning",
            showCancelButton: true,
        });
        if (!res.isConfirmed) return;
        await deleteDoc(doc(db, "otherIncome", id));
        Swal.fire("Deleted", "Income removed", "success");
        const snap = await getDocs(collection(db, "otherIncome"));
        setIncome(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    // Filter & paginate
    const filtered = income.filter(i =>
        i.description?.toLowerCase().includes(search.toLowerCase()) ||
        i.userId?.toLowerCase().includes(search.toLowerCase()) ||
        (incomeHeads.find(h => h.id === i.incomeHeadId)?.name || "")
            .toLowerCase()
            .includes(search.toLowerCase())
    );

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Box p={2} bgcolor="#f4f6f8" minHeight="100vh">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight={800}>
                    Other Income
                </Typography>
                <Button variant="contained" onClick={() => setOpenDialog(true)}>
                    Add New Income
                </Button>
            </Box>

            <TextField
                label="Search by head, description, user"
                value={search}
                onChange={e => setSearch(e.target.value)}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
            />

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Income Head</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Description</TableCell>
                                {/* <TableCell>User ID</TableCell> */}
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginated.map((i, index) => (
                                <TableRow key={i.id}>
                                    <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                                    <TableCell>
                                        {incomeHeads.find(h => h.id === i.incomeHeadId)?.name || i.incomeHeadId}
                                    </TableCell>
                                    <TableCell>{i.date?.toDate().toLocaleDateString()}</TableCell>
                                    <TableCell>{i.amount}</TableCell>
                                    <TableCell>{i.description || "-"}</TableCell>
                                    {/* <TableCell>{i.userId || "-"}</TableCell> */}
                                    <TableCell align="center">
                                        <IconButton color="primary" onClick={() => handleEdit(i)}>
                                            <EditOutlined />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDelete(i.id)}>
                                            <DeleteOutline />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Box display="flex" justifyContent="center" mt={2}>
                        <Pagination
                            count={Math.ceil(filtered.length / PAGE_SIZE)}
                            page={page}
                            onChange={(e, v) => setPage(v)}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* ================= Dialog ================= */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? "Edit Income" : "Add New Income"}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Income Head</InputLabel>
                        <Select name="incomeHeadId" value={form.incomeHeadId} onChange={handleChange}>
                            {incomeHeads.map(h => (
                                <MenuItem key={h.id} value={h.id}>
                                    {h.name || h.id}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Date"
                        name="date"
                        type="date"
                        value={form.date}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Amount"
                        name="amount"
                        type="number"
                        value={form.amount}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Description"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                    />

                  
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>
                        {editingId ? "Update" : "Add"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Otherincome;